import { useState, useRef, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getTicketDraft,
  regenerateTicketDraft,
  getTicketDraftConversation,
  sendTicketDraftChat,
  type TicketCategory,
  type DraftConversationMessage,
} from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Loader2,
  Sparkles,
  RefreshCw,
  AlertTriangle,
  FlaskConical,
  Send,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { TicketWorkflowTrace } from "./ticket-workflow-trace";
import { formatDistanceToNow } from "date-fns";

interface AIDraftSectionProps {
  ticketId: string;
  ticketStatus: string;
  currentCategory: TicketCategory;
  onCategoryChange?: (category: TicketCategory) => void;
}

export function AIDraftSection({
  ticketId,
  ticketStatus,
  currentCategory,
  onCategoryChange,
}: AIDraftSectionProps) {
  const queryClient = useQueryClient();
  const [showTrace, setShowTrace] = useState(false);
  const [chatInput, setChatInput] = useState("");
  const chatEndRef = useRef<HTMLDivElement>(null);

  // Fetch draft with polling while generating
  const { data, isLoading } = useQuery({
    queryKey: ["ticket-draft", ticketId],
    queryFn: () => getTicketDraft(ticketId),
    refetchInterval: (query) => {
      const draft = query.state.data?.draft;
      // Poll every 2 seconds while draft is not ready or still generating
      if (!draft || draft.status === "pending" || draft.status === "generating") {
        return 2000;
      }
      return false;
    },
  });

  const draft = data?.draft;

  // Fetch conversation history (for follow-up chat)
  const { data: conversationData } = useQuery({
    queryKey: ["ticket-draft-conversation", ticketId],
    queryFn: () => getTicketDraftConversation(ticketId),
    enabled: draft?.status === "completed",
  });

  const conversation = conversationData?.conversation;
  const messages = conversation?.messages || [];

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages.length]);

  const regenerateMutation = useMutation({
    mutationFn: () => regenerateTicketDraft(ticketId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ticket-draft", ticketId] });
      queryClient.invalidateQueries({ queryKey: ["ticket-draft-conversation", ticketId] });
    },
  });

  const chatMutation = useMutation({
    mutationFn: (message: string) => sendTicketDraftChat(ticketId, message),
    onSuccess: () => {
      setChatInput("");
      queryClient.invalidateQueries({ queryKey: ["ticket-draft-conversation", ticketId] });
    },
  });

  const handleSendMessage = () => {
    if (chatInput.trim() && !chatMutation.isPending) {
      chatMutation.mutate(chatInput.trim());
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Show loading state while fetching or if draft not ready yet
  if (isLoading || !draft) {
    return (
      <div className="border rounded-lg bg-blue-50/50 p-4">
        <div className="flex items-center gap-2 text-blue-700">
          <Loader2 className="h-4 w-4 animate-spin" />
          <Sparkles className="h-4 w-4" />
          <span className="font-medium">Preparing AI response...</span>
        </div>
        <p className="text-sm text-blue-600 mt-2">
          OREO is analyzing the conversation.
        </p>
      </div>
    );
  }

  // Don't show if already reviewed
  if (draft.review_status !== "pending") {
    return null;
  }

  // Don't show for closed tickets
  if (ticketStatus === "closed" || ticketStatus === "archived") {
    return null;
  }

  // Generating state
  if (draft.status === "pending" || draft.status === "generating") {
    return (
      <div className="border rounded-lg bg-blue-50/50 p-4">
        <div className="flex items-center gap-2 text-blue-700">
          <Loader2 className="h-4 w-4 animate-spin" />
          <Sparkles className="h-4 w-4" />
          <span className="font-medium">Generating AI response...</span>
        </div>
        <p className="text-sm text-blue-600 mt-2">
          OREO is drafting a response based on the customer's message.
        </p>
      </div>
    );
  }

  // Error state
  if (draft.status === "error") {
    return (
      <div className="border rounded-lg bg-red-50/50 p-4">
        <div className="flex items-center gap-2 text-red-700">
          <AlertTriangle className="h-4 w-4" />
          <span className="font-medium">Failed to generate response</span>
        </div>
        <p className="text-sm text-red-600 mt-2">
          {draft.error_message || "An error occurred while generating the draft."}
        </p>
        <Button
          size="sm"
          variant="outline"
          className="mt-3"
          onClick={() => regenerateMutation.mutate()}
          disabled={regenerateMutation.isPending}
        >
          {regenerateMutation.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
          ) : (
            <RefreshCw className="h-4 w-4 mr-2" />
          )}
          Try Again
        </Button>
      </div>
    );
  }

  // Completed state - show draft with chat capability
  const content = draft.phase4_formatted_response ?? "";

  return (
    <div className="border rounded-lg bg-gradient-to-r from-blue-50/80 to-purple-50/50 p-4 flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-3 flex-shrink-0">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-purple-600" />
          <span className="font-medium text-sm">AI Draft Response</span>
          <Badge variant="outline" className="text-xs text-muted-foreground">
            View only
          </Badge>
        </div>
        <div className="flex items-center gap-1">
          <Button
            size="sm"
            variant="ghost"
            className="h-6 px-2 text-muted-foreground hover:text-foreground"
            onClick={() => setShowTrace(true)}
            title="View execution trace"
          >
            <FlaskConical className="h-3 w-3" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="h-6 px-2 text-muted-foreground hover:text-foreground"
            onClick={() => regenerateMutation.mutate()}
            disabled={regenerateMutation.isPending}
          >
            {regenerateMutation.isPending ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <RefreshCw className="h-3 w-3" />
            )}
          </Button>
        </div>
      </div>

      {/* Category suggestion */}
      {draft.suggested_category && draft.suggested_category !== currentCategory && (
        <div className="flex items-center gap-2 mb-3 p-2 bg-white/60 rounded-md border border-purple-100 flex-shrink-0">
          <span className="text-xs text-muted-foreground">Suggested category:</span>
          <Badge
            variant="outline"
            className="text-xs bg-purple-100 text-purple-700 border-purple-200 cursor-pointer hover:bg-purple-200"
            onClick={() => onCategoryChange?.(draft.suggested_category as TicketCategory)}
          >
            {draft.suggested_category}
            <span className="ml-1 text-purple-500">AI</span>
          </Badge>
          {draft.category_confidence !== null && (
            <span className="text-xs text-muted-foreground">
              ({Math.round(draft.category_confidence * 100)}% confident)
            </span>
          )}
        </div>
      )}

      {/* Chat messages - always visible */}
      <div className="flex-1 min-h-0 overflow-y-auto rounded-md border bg-white/80 p-3 space-y-3">
        {messages.length > 0 ? (
          <>
            {messages.map((msg: DraftConversationMessage, index: number) => (
              <div
                key={msg.id}
                className={cn(
                  "flex flex-col max-w-[85%]",
                  msg.role === "user" ? "ml-auto items-end" : "mr-auto items-start"
                )}
              >
                {/* Sender label */}
                <div className={cn(
                  "flex items-center gap-1 mb-1 px-1",
                  msg.role === "user" ? "justify-end" : "justify-start"
                )}>
                  {msg.role === "assistant" ? (
                    <>
                      <Sparkles className="h-3 w-3 text-purple-600" />
                      <span className="text-xs font-medium text-purple-600">
                        {index === 0 ? "Oreo Draft" : "Oreo"}
                      </span>
                      {index === 0 && draft.total_duration_ms && (
                        <span className="text-[10px] text-muted-foreground">
                          ({(draft.total_duration_ms / 1000).toFixed(1)}s)
                        </span>
                      )}
                    </>
                  ) : (
                    <span className="text-xs font-medium text-gray-600">You</span>
                  )}
                </div>
                <div
                  className={cn(
                    "px-3 py-2 rounded-lg text-sm",
                    msg.role === "user"
                      ? "bg-purple-100 text-purple-900"
                      : "bg-gray-100 text-gray-900"
                  )}
                >
                  <p className="whitespace-pre-wrap">{msg.content}</p>
                </div>
                <span className="text-[10px] text-muted-foreground mt-1 px-1">
                  {formatDistanceToNow(new Date(msg.createdAt), { addSuffix: true })}
                </span>
              </div>
            ))}

            {/* Loading indicator for pending response */}
            {chatMutation.isPending && (
              <div className="flex items-start gap-2 mr-auto">
                <div className="px-3 py-2 rounded-lg bg-gray-100 text-gray-900 text-sm">
                  <div className="flex items-center gap-2">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    <span>Thinking...</span>
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          /* Fallback: show raw content if no messages yet */
          <div className="text-sm whitespace-pre-wrap">{content}</div>
        )}

        <div ref={chatEndRef} />
      </div>

      {/* Always visible input */}
      <div className="flex gap-2 mt-3 flex-shrink-0">
        <Input
          value={chatInput}
          onChange={(e) => setChatInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Ask oreo a follow-up question..."
          disabled={chatMutation.isPending}
          className="flex-1 text-sm"
        />
        <Button
          size="sm"
          onClick={handleSendMessage}
          disabled={!chatInput.trim() || chatMutation.isPending}
        >
          {chatMutation.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Send className="h-4 w-4" />
          )}
        </Button>
      </div>

      {/* Error message */}
      {chatMutation.isError && (
        <p className="text-xs text-red-600 mt-1">
          Failed to send message. Please try again.
        </p>
      )}

      {/* Trace Dialog */}
      <Dialog open={showTrace} onOpenChange={setShowTrace}>
        <DialogContent className="max-w-[95vw] w-[1200px] h-[85vh] max-h-[800px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FlaskConical className="h-5 w-5 text-purple-600" />
              AI Execution Trace
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-hidden">
            <TicketWorkflowTrace ticketId={ticketId} />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
